import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { formsApi } from '../services/formsApi';
import { findRoutingField, routingOptionLabel } from '../utils/formLabels';
import { toast } from 'react-toastify';
import StatCard from './StatCard';
import PermissionTooltip from './PermissionTooltip';
import { 
  Users, 
  UserPlus, 
  Search, 
  Edit, 
  UserX,
  Shield, 
  Building, 
  Mail, 
  User,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  UserCheck,
  Crown,
  UserCog,
  Info
} from 'lucide-react';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  // Estados para filtros e paginação
  const [filters, setFilters] = useState({
    search: '',
    role: 'all',
    status: 'all'
  });
  const [pagination, setPagination] = useState({
    current: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [stats, setStats] = useState({
    total_users: 0,
    role_distribution: []
  });

  // Formulários de todos os clientes, usados para montar dinamicamente as opções
  // de "Nível de Acesso" a partir da pergunta que divide a visualização em cada um.
  const [clientForms, setClientForms] = useState([]);

  // Rótulos amigáveis para os slugs legados, usados só ao exibir um nível
  // defasado que o usuário já possui mas que não existe (mais) no formulário.
  const LEGACY_LEVEL_LABELS = {
    diretores: 'Diretores/Vice-Presidência',
    presidencia: 'Presidência',
    demais_colaboradores: 'Demais Colaboradores'
  };

  // A pergunta que divide a visualização no formulário do cliente (sobre_quem
  // por padrão, ou unidade/filial... — ver findRoutingField) é a ÚNICA fonte da
  // verdade das categorias de acesso (o valor gravado em complaints.sobre_quem
  // é exatamente esse). Retorna o campo (ou null sem formulário ou sem divisão).
  const getSobreQuemField = (clientSlug) => {
    const form = clientForms.find(f => f.client_slug === clientSlug);
    return findRoutingField(form?.fields);
  };

  // Opções de "Nível de Acesso" para o client_slug informado. Fonte = categorias
  // do sobre_quem do formulário. Além delas, incluímos quaisquer níveis que o
  // usuário JÁ possua mas que não existam (mais) no formulário — marcados como
  // `stale` — para que a edição nunca esconda (e mantenha em silêncio) uma
  // atribuição defasada, que hoje faria o admin não ver denúncia nenhuma.
  // Sem formulário ainda para o cliente, a lista vem vazia e a UI orienta o
  // administrador em vez de oferecer categorias falsas.
  const getAdminLevelOptions = (clientSlug) => {
    const sobreQuemField = getSobreQuemField(clientSlug);
    const rawOptions = Array.isArray(sobreQuemField?.options) ? sobreQuemField.options : [];

    const fromForm = rawOptions
      .map(option => {
        const isObjectOption = typeof option === 'object' && option !== null;
        const value = isObjectOption ? option.value : option;
        const label = isObjectOption ? (option.label || option.value) : option;
        return value
          ? { value, label, description: `Acesso às denúncias de ${label}`, stale: false }
          : null;
      })
      .filter(Boolean);

    const known = new Set(fromForm.map(o => o.value));
    const stale = (formData.admin_levels || [])
      .filter(level => level && !known.has(level))
      .map(level => ({
        value: level,
        label: LEGACY_LEVEL_LABELS[level] || level,
        description: '⚠️ Categoria não existe (mais) no formulário deste cliente — remova ou ajuste',
        stale: true
      }));

    return [...fromForm, ...stale];
  };

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    role: 'advogado',
    is_admin: false,
    powerbi_links: '',
    client_slug: '',
    client_name: '',
    is_client_admin: false,
    is_investigador_externo: false,
    admin_levels: []
  });

  const getRoleDisplay = (role, isClientAdmin, isInvestigadorExterno) => {
    const roleMap = {
      'super_admin': 'Super Admin',
      'admin': 'Administrador',
      'client_admin': 'Admin Cliente',
      'investigador_externo': 'Investigador Externo',
      'advogado': 'Advogado',
      'user': 'Usuário'
    };
    
    if (isClientAdmin) return 'Admin Cliente';
    if (isInvestigadorExterno) return 'Investigador Externo';
    return roleMap[role] || role;
  };

  const getRoleColor = (role, isClientAdmin, isInvestigadorExterno) => {
    if (isClientAdmin) return 'bg-purple-100 text-purple-800';
    if (isInvestigadorExterno) return 'bg-orange-100 text-orange-800';
    if (role === 'super_admin') return 'bg-red-100 text-red-800';
    if (role === 'admin') return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getLevelInfo = (level, clientSlug) => {
    // Slugs legados têm rótulo/ícone/cor fixos. Categorias customizadas do
    // Sobre Quem (onde valor = texto exibido) usam o próprio valor como rótulo.
    // O texto da opção no formulário do cliente sempre vence: o slug não diz
    // nada (na atomplast, `presidencia` é "Recursos Humanos/Supervisão PCP").
    const form = clientForms.find(f => f.client_slug === clientSlug);
    const formLabel = routingOptionLabel(form?.fields, level);
    const levelMap = {
      'admin_cliente1': {
        label: 'Diretores/Vice-Presidência',
        icon: <Building className="h-3 w-3" />,
        color: 'text-purple-600',
        bgColor: 'bg-purple-100'
      },
      'admin_cliente2': {
        label: 'Presidência',
        icon: <UserCheck className="h-3 w-3" />,
        color: 'text-red-600',
        bgColor: 'bg-red-100'
      },
      'admin_cliente3': {
        label: 'Demais Colaboradores',
        icon: <Users className="h-3 w-3" />,
        color: 'text-green-600',
        bgColor: 'bg-green-100'
      },
      'diretores': {
        label: LEGACY_LEVEL_LABELS.diretores,
        icon: <Building className="h-3 w-3" />,
        color: 'text-purple-600',
        bgColor: 'bg-purple-100'
      },
      'presidencia': {
        label: LEGACY_LEVEL_LABELS.presidencia,
        icon: <UserCheck className="h-3 w-3" />,
        color: 'text-red-600',
        bgColor: 'bg-red-100'
      },
      'demais_colaboradores': {
        label: LEGACY_LEVEL_LABELS.demais_colaboradores,
        icon: <Users className="h-3 w-3" />,
        color: 'text-green-600',
        bgColor: 'bg-green-100'
      }
    };

    const info = levelMap[level] || {
      label: level, // categoria customizada: o valor já é o texto legível
      icon: <Users className="h-3 w-3" />,
      color: 'text-gray-600',
      bgColor: 'bg-gray-100'
    };
    return formLabel ? { ...info, label: formLabel } : info;
  };

  const renderAdminLevels = (user) => {
    if (!user.is_client_admin || !user.admin_levels) {
      return null;
    }

    try {
      const levels = JSON.parse(user.admin_levels);
      if (levels.length === 0) return null;

      return (
        <div className="flex flex-wrap gap-1 mt-1">
          {levels.map((level, index) => {
            const levelInfo = getLevelInfo(level, user.client_slug);
            return (
              <span
                key={index}
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${levelInfo.bgColor} ${levelInfo.color}`}
              >
                {levelInfo.icon}
                <span className="ml-1">{levelInfo.label}</span>
              </span>
            );
          })}
        </div>
      );
    } catch (error) {
      return null;
    }
  };

  const loadUsers = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
        ...(filters.search && { search: filters.search }),
        ...(filters.role !== 'all' && { role: filters.role }),
        ...(filters.status !== 'all' && { status: filters.status })
      });

      const response = await api.get(`/users?${params}`);
      const data = response.data;
      
      setUsers(data.users || []);
      setPagination({
        current: data.pagination?.current || page,
        limit: data.pagination?.limit || 20,
        total: data.pagination?.total || 0,
        pages: data.pagination?.pages || 0
      });
      setStats({
        total_users: data.pagination?.total || 0,
        role_distribution: data.role_distribution || []
      });
    } catch (error) {
      console.error('❌ Erro ao carregar usuários:', error);
      toast.error(`Erro ao carregar usuários: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers(1);
  }, []);

  useEffect(() => {
    const loadClientForms = async () => {
      try {
        const response = await formsApi.getForms();
        const formsData = Array.isArray(response) ? response : (response?.data || response?.forms || []);
        setClientForms(formsData);
      } catch (error) {
        console.error('❌ Erro ao carregar formulários para Níveis de Acesso:', error);
      }
    };
    loadClientForms();
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    loadUsers(1);
  };

  const handlePageChange = (page) => {
    loadUsers(page);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Validação para client-admin
      if (formData.is_client_admin) {
        if (!formData.client_slug || formData.client_slug.trim() === '') {
          toast.error('Client-admin deve ter um client_slug definido');
          setLoading(false);
          return;
        }
        if (!formData.client_name || formData.client_name.trim() === '') {
          toast.error('Client-admin deve ter um client_name definido');
          setLoading(false);
          return;
        }
        // admin_levels vazio é permitido de propósito: o backend trata isso como
        // "vê TODAS as denúncias do cliente". É a escolha segura quando o
        // formulário (e suas categorias de Sobre Quem) ainda não existe — o
        // admin pode ser restringido depois, editando este usuário.
      }

      const userData = {
        username: formData.username,
        email: formData.email,
        fullName: formData.full_name,
        role: formData.is_client_admin ? 'client_admin' : formData.role, // Forçar role correto
        isAdmin: formData.is_admin,
        powerbiLinks: formData.powerbi_links,
        client_slug: formData.client_slug,
        client_name: formData.client_name,
        isClientAdmin: formData.is_client_admin,
        isInvestigadorExterno: formData.is_investigador_externo,
        admin_levels: formData.admin_levels
      };
      
      if (!editingUser) {
        userData.username = formData.username.trim();
      }
      
      if (formData.password && formData.password.trim() !== '') {
        userData.password = formData.password;
      }
      
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, userData);
        toast.success('Usuário atualizado com sucesso!');
      } else {
        await api.post('/users', userData);
        toast.success('Usuário criado com sucesso!');
      }
      
      setShowCreateForm(false);
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        password: '',
        full_name: '',
        role: 'advogado',
        is_admin: false,
        powerbi_links: '',
        client_slug: '',
        client_name: '',
        is_client_admin: false,
        is_investigador_externo: false,
        admin_levels: []
      });
      loadUsers();
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      if (error.response?.data?.details && Array.isArray(error.response.data.details)) {
        const errorMessages = error.response.data.details.map(detail => detail.msg).join(', ');
        toast.error(`Dados inválidos: ${errorMessages}`);
      } else {
        toast.error(error.response?.data?.error || 'Erro ao salvar usuário');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (userToEdit) => {
    console.log('🔍 Editando usuário:', userToEdit);
    console.log('🔍 admin_levels bruto:', userToEdit.admin_levels);
    console.log('🔍 Tipo do admin_levels:', typeof userToEdit.admin_levels);

    setEditingUser(userToEdit);

    // Converter admin_levels de string JSON para array
    let adminLevelsArray = [];
    if (userToEdit.admin_levels) {
      try {
        adminLevelsArray = typeof userToEdit.admin_levels === 'string'
          ? JSON.parse(userToEdit.admin_levels)
          : userToEdit.admin_levels;
        console.log('✅ admin_levels convertido:', adminLevelsArray);
      } catch (error) {
        console.error('❌ Erro ao parsear admin_levels:', error);
        adminLevelsArray = [];
      }
    }

    const newFormData = {
      username: userToEdit.username,
      email: userToEdit.email || '',
      password: '',
      full_name: userToEdit.full_name,
      role: userToEdit.role,
      is_admin: userToEdit.is_admin,
      powerbi_links: userToEdit.powerbi_links || '',
      client_slug: userToEdit.client_slug || '',
      client_name: userToEdit.client_name || '',
      is_client_admin: userToEdit.is_client_admin,
      is_investigador_externo: userToEdit.is_investigador_externo,
      admin_levels: adminLevelsArray
    };

    console.log('📝 FormData final:', newFormData);
    console.log('📝 Email do usuário sendo editado:', userToEdit.email);
    console.log('📝 Email no formData:', newFormData.email);

    setFormData(newFormData);
    setShowCreateForm(true);
  };

  const handleToggleActive = async (user) => {
    const isCurrentlyActive = user.is_active !== false;
    const nextIsActive = !isCurrentlyActive;
    const actionLabel = nextIsActive ? 'ativar' : 'inativar';

    if (!window.confirm(`Tem certeza que deseja ${actionLabel} este usuário?`)) {
      return;
    }

    try {
      await api.put(`/users/${user.id}`, { isActive: nextIsActive });
      toast.success(`Usuário ${nextIsActive ? 'ativado' : 'inativado'} com sucesso!`);
      loadUsers(pagination.current);
    } catch (error) {
      toast.error(error.response?.data?.error || `Erro ao ${actionLabel} usuário`);
    }
  };

  return (
    <div className="rf-screen">
      <div>

        {/* O cabeçalho sai de dentro do cartão branco: é o topo da página, não
            conteúdo. O ícone em caixa com gradiente saiu junto — o título já
            diz o que é a tela. A profundidade de <div> é a mesma de antes,
            senão os fechamentos do resto do arquivo não batem. */}
        <div>
          <div>
            <div className="rf-screen__head">
              <div>
                <h1 className="rf-screen__title">Usuários</h1>
                <p className="rf-screen__sub">Contas e permissões de acesso ao sistema</p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => loadUsers(pagination.current)}
                  className="p-2 text-gray-600 hover:text-[#149edb] hover:bg-gray-100 rounded-lg transition-colors"
                  disabled={loading}
                >
                  <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => {
                    setShowCreateForm(true);
                    setEditingUser(null);
                    setFormData({
                      username: '',
                      email: '',
                      password: '',
                      full_name: '',
                      role: 'advogado',
                      is_admin: false,
                      powerbi_links: '',
                      client_slug: '',
                      client_name: '',
                      is_client_admin: false,
                      is_investigador_externo: false,
                      admin_levels: []
                    });
                  }}
                  className="bg-gradient-to-r from-[#149edb] to-[#0d7bb8] text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:from-[#0d7bb8] hover:to-[#0a6b9f] transition-all duration-200 flex items-center space-x-2"
                >
                  <UserPlus className="w-5 h-5" />
                  <span>Novo Usuário</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div 
          className="gap-4 mb-8" 
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '1rem'
          }}
        >
          <StatCard
            title="Total de Usuários"
            value={stats.total_users}
            icon={Users}
            color="fius"
            subtitle="Cadastrados no sistema"
          />
          <StatCard
            title="Administradores"
            value={users.filter(u => u.is_admin).length}
            icon={Shield}
            color="green"
            subtitle="Acesso total"
          />
          <StatCard
            title="Admin Cliente"
            value={users.filter(u => u.is_client_admin).length}
            icon={Building}
            color="purple"
            subtitle="Por empresa"
          />
          <StatCard
            title="Investigadores"
            value={users.filter(u => u.is_investigador_externo).length}
            icon={UserCheck}
            color="orange"
            subtitle="Externos"
          />
          <StatCard
            title="Advogados"
            value={users.filter(u => u.role === 'advogado').length}
            icon={UserCog}
            color="blue"
            subtitle="Profissionais"
          />
          <StatCard
            title="Usuários Comuns"
            value={users.filter(u => u.role === 'user').length}
            icon={User}
            color="indigo"
            subtitle="Básicos"
          />
        </div>

        {/* Create/Edit Form */}
        {showCreateForm && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 mb-8">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-[#149edb]/10 to-[#0d7bb8]/10 rounded-t-2xl">
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                <UserPlus className="w-6 h-6 mr-2" />
                {editingUser ? 'Editar Usuário' : 'Criar Novo Usuário'}
              </h3>
              <p className="text-gray-600 text-sm mt-1">
                {editingUser ? 'Atualize as informações do usuário abaixo' : 'Preencha todos os campos obrigatórios para criar um novo usuário'}
              </p>
            </div>
            <div className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nome de Usuário *
                    </label>
                    <input
                      type="text"
                      required={!editingUser}
                      disabled={editingUser}
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className={`w-full px-4 py-3 border-2 rounded-xl font-medium transition-all duration-300 focus:outline-none ${
                        editingUser 
                          ? 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed' 
                          : 'border-gray-300 hover:border-[#149edb] focus:border-[#149edb] focus:ring-4 focus:ring-[#149edb]/20'
                      }`}
                      placeholder={editingUser ? 'Nome não pode ser alterado' : 'Digite o nome de usuário'}
                      minLength="3"
                      maxLength="50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      E-mail *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl font-medium hover:border-[#149edb] focus:border-[#149edb] focus:ring-4 focus:ring-[#149edb]/20 focus:outline-none transition-all duration-300"
                      maxLength="150"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl font-medium hover:border-[#149edb] focus:border-[#149edb] focus:ring-4 focus:ring-[#149edb]/20 focus:outline-none transition-all duration-300"
                      minLength="2"
                      maxLength="100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {editingUser ? 'Nova Senha (deixe em branco para não alterar)' : 'Senha *'}
                    </label>
                    <input
                      type="password"
                      required={!editingUser}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl font-medium hover:border-[#149edb] focus:border-[#149edb] focus:ring-4 focus:ring-[#149edb]/20 focus:outline-none transition-all duration-300"
                      placeholder={editingUser ? 'Deixe em branco para não alterar a senha' : 'Digite a senha'}
                      minLength="6"
                      maxLength="50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Cargo
                    </label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl font-medium hover:border-[#149edb] focus:border-[#149edb] focus:ring-4 focus:ring-[#149edb]/20 focus:outline-none transition-all duration-300"
                    >
                      <option value="advogado">Advogado</option>
                      <option value="user">Usuário</option>
                      <option value="admin">Administrador</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Empresa
                    </label>
                    <input
                      type="text"
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl font-medium hover:border-[#149edb] focus:border-[#149edb] focus:ring-4 focus:ring-[#149edb]/20 focus:outline-none transition-all duration-300"
                      placeholder="Nome da empresa"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Client Slug
                    </label>
                    <input
                      type="text"
                      value={formData.client_slug}
                      onChange={(e) => setFormData({ ...formData, client_slug: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl font-medium hover:border-[#149edb] focus:border-[#149edb] focus:ring-4 focus:ring-[#149edb]/20 focus:outline-none transition-all duration-300"
                      placeholder="client-slug (ex: vulkan, daterra)"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_admin}
                      onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                      className="w-4 h-4 text-[#149edb] border-gray-300 rounded focus:ring-[#149edb]"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">Administrador do Sistema</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_client_admin}
                      onChange={(e) => setFormData({ ...formData, is_client_admin: e.target.checked })}
                      className="w-4 h-4 text-[#149edb] border-gray-300 rounded focus:ring-[#149edb]"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">Admin da Empresa</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_investigador_externo}
                      onChange={(e) => setFormData({ ...formData, is_investigador_externo: e.target.checked })}
                      className="w-4 h-4 text-[#149edb] border-gray-300 rounded focus:ring-[#149edb]"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">Investigador Externo</span>
                  </label>
                </div>

                {/* Campo de níveis de administração - só aparece para client_admin */}
                {formData.is_client_admin && (
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Níveis de Acesso
                      <span className="text-gray-500 text-xs ml-1">(Selecione um ou mais níveis)</span>
                    </label>
                    {/* Aviso quando ainda não há formulário (com campo Sobre Quem)
                        para o client_slug: evita oferecer categorias falsas que
                        depois não casariam com as denúncias reais. */}
                    {!getSobreQuemField(formData.client_slug) && (
                      <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <strong>ℹ️ O formulário do client_slug "{formData.client_slug || '—'}" não divide a
                        visualização (ou ainda não existe).</strong>
                        <p className="mt-1">
                          As categorias de acesso vêm da pergunta marcada como "Divide a visualização entre os
                          admins" no formulário (ex.: sobre quem, unidade, filial). Se o cliente quer essa divisão,
                          configure o formulário primeiro (em Form Management) e só depois marque os níveis aqui.
                        </p>
                        <p className="mt-1">
                          Se salvar <strong>sem nenhum nível marcado</strong>, este Admin da Empresa
                          verá <strong>todas</strong> as denúncias do cliente — você pode restringir
                          depois, editando este usuário quando o formulário existir.
                        </p>
                      </div>
                    )}

                    {getAdminLevelOptions(formData.client_slug).length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {getAdminLevelOptions(formData.client_slug).map((level) => (
                          <label
                            key={level.value}
                            className={`flex items-center p-3 border rounded-lg cursor-pointer ${
                              level.stale
                                ? 'border-amber-300 bg-amber-50 hover:bg-amber-100'
                                : 'border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={formData.admin_levels.includes(level.value)}
                              onChange={(e) => {
                                const newLevels = e.target.checked
                                  ? [...formData.admin_levels, level.value]
                                  : formData.admin_levels.filter(l => l !== level.value);
                                setFormData({ ...formData, admin_levels: newLevels });
                              }}
                              className="w-4 h-4 text-[#149edb] border-gray-300 rounded focus:ring-[#149edb]"
                            />
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-700 flex items-center">
                                <Building className={`h-4 w-4 mr-1 ${level.stale ? 'text-amber-600' : 'text-purple-600'}`} />
                                {level.label}
                              </div>
                              <div className={`text-xs ${level.stale ? 'text-amber-700' : 'text-gray-500'}`}>
                                {level.description}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-gray-500">
                      Categorias lidas da pergunta "{getSobreQuemField(formData.client_slug)?.label || 'Sobre Quem'}", que divide a visualização do client_slug informado acima
                      (Form Management). Renomear ou remover uma categoria lá exige revisar as permissões aqui.
                    </p>
                    {formData.admin_levels.length === 0 && getSobreQuemField(formData.client_slug) && (
                      <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
                        ⚠️ Sem nenhum nível marcado, este Admin da Empresa verá todas as denúncias do cliente.
                      </p>
                    )}
                    
                    {/* Resumo das Permissões */}
                    {formData.is_client_admin && formData.admin_levels.length > 0 && (
                      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center">
                          <Info className="h-4 w-4 mr-1" />
                          Resumo das Permissões
                        </h4>
                        <div className="text-sm text-blue-800">
                          <p className="mb-2">Este usuário poderá:</p>
                          <ul className="list-disc list-inside space-y-1 ml-2">
                            <li>Ver formulários do cliente {formData.client_name || '[Cliente]'}</li>
                            <li>Ver dashboard do cliente {formData.client_name || '[Cliente]'}</li>
                            <li>Gerenciar usuários do cliente {formData.client_name || '[Cliente]'}</li>
                            <li>Acessar logs de auditoria do cliente</li>
                            {formData.admin_levels.map(level => {
                              const levelInfo = getLevelInfo(level, formData.client_slug);
                              return (
                                <li key={level}>Ver denúncias de {levelInfo.label}</li>
                              );
                            })}
                          </ul>
                          <p className="mt-2 text-blue-700 font-medium">
                            ❌ Não poderá ver dados de outros clientes ou configurações globais
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-end space-x-4 pt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setEditingUser(null);
                    }}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-3 bg-gradient-to-r from-[#149edb] to-[#0d7bb8] text-white rounded-xl font-medium hover:from-[#0d7bb8] hover:to-[#0a6b9f] transition-all duration-200 disabled:opacity-50"
                  >
                    {loading ? 'Salvando...' : editingUser ? 'Atualizar' : 'Criar Usuário'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Users List */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <Users className="w-6 h-6 mr-2" />
                Usuários Cadastrados
              </h2>
              <div className="text-sm text-gray-600">
                Total: {stats.total_users} usuários • Página {pagination.current} de {pagination.pages}
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Buscar usuários..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#149edb] focus:border-[#149edb]"
                  />
                </div>
              </div>
              <div className="flex gap-4">
                <select
                  value={filters.role}
                  onChange={(e) => handleFilterChange('role', e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#149edb] focus:border-[#149edb]"
                >
                  <option value="all">Todas as funções</option>
                  <option value="admin">Administrador</option>
                  <option value="advogado">Advogado</option>
                  <option value="user">Usuário</option>
                </select>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#149edb] focus:border-[#149edb]"
                >
                  <option value="all">Todos os status</option>
                  <option value="active">Ativos</option>
                  <option value="inactive">Inativos</option>
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#149edb]"></div>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuário</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">E-mail</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Função</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array.isArray(users) && users.length > 0 ? (
                    users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-r from-[#149edb] to-[#0d7bb8] rounded-full flex items-center justify-center text-white font-semibold">
                              {user.username?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{user.username || 'N/A'}</div>
                              <div className="text-sm text-gray-500">ID: {user.id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.full_name || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900">
                            <Mail className="w-4 h-4 mr-2 text-gray-400" />
                            {user.email || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <PermissionTooltip user={user} getLevelLabel={(level) => getLevelInfo(level, user.client_slug).label}>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role, user.is_client_admin, user.is_investigador_externo)} cursor-help`}>
                              {getRoleDisplay(user.role, user.is_client_admin, user.is_investigador_externo)}
                              <Info className="h-3 w-3 ml-1 opacity-60" />
                            </span>
                          </PermissionTooltip>
                          {renderAdminLevels(user)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900">
                            <Building className="w-4 h-4 mr-2 text-gray-400" />
                            {user.client_name || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleEdit(user)}
                              className="text-[#149edb] hover:text-[#0d7bb8] p-2 rounded-lg hover:bg-[#149edb]/10 transition-colors"
                              title="Editar usuário"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleToggleActive(user)}
                              className={`p-2 rounded-lg transition-colors ${
                                user.is_active !== false
                                  ? 'text-red-600 hover:text-red-800 hover:bg-red-50'
                                  : 'text-green-600 hover:text-green-800 hover:bg-green-50'
                              }`}
                              title={user.is_active !== false ? 'Inativar usuário' : 'Ativar usuário'}
                            >
                              {user.is_active !== false ? (
                                <UserX className="w-4 h-4" />
                              ) : (
                                <UserCheck className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center">
                          <Users className="w-12 h-12 text-gray-400 mb-4" />
                          <p className="text-gray-500 text-lg font-medium">Nenhum usuário encontrado</p>
                          <p className="text-gray-400 text-sm mt-1">
                            {loading ? 'Carregando usuários...' : 'Tente ajustar os filtros de busca'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Mostrando {((pagination.current - 1) * pagination.limit) + 1} a {Math.min(pagination.current * pagination.limit, pagination.total)} de {pagination.total} usuários
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.current - 1)}
                    disabled={pagination.current === 1}
                    className="p-2 text-gray-500 hover:text-[#149edb] hover:bg-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          pagination.current === page
                            ? 'bg-[#149edb] text-white'
                            : 'text-gray-700 hover:bg-white hover:text-[#149edb]'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handlePageChange(pagination.current + 1)}
                    disabled={pagination.current === pagination.pages}
                    className="p-2 text-gray-500 hover:text-[#149edb] hover:bg-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
